// Per-tick working context threaded through systems. `s` is the draft state
// (a structuredClone of the input state) that systems mutate freely.

import type {
  AllocationReport,
  Command,
  ContentPack,
  Discipline,
  EngineEvent,
  GameState,
  LedgerCategory,
  LedgerEntry,
  ProductSaleSample,
} from "./types.js";
import type { Rng } from "./prng.js";
import { makeRng } from "./prng.js";

export interface TickContext {
  s: GameState;
  content: ContentPack;
  rng: Rng;
  monthsPerTick: number;
  cashAtTickStart: number;
  ledger: LedgerEntry[];
  events: EngineEvent[];
  rejected: { command: Command; reason: string }[];
  /** Engineer-month budget remaining this tick, per discipline (set by people system). */
  emBudget: Record<Discipline, number>;
  /** Where every consumed engineer-month went (invariant 4 observability). */
  allocation: Record<Discipline, AllocationReport>;
  /** Per-product market actuals this tick (mirrors the ledger, structured). */
  productSales: ProductSaleSample[];
  /** Tick aggregates for KPI/runway. */
  revenue: number; // product revenue only
  nreRevenue: number;
  cogs: number;
  opex: number;
  financingInflow: number;
}

export function makeCtx(s: GameState, content: ContentPack): TickContext {
  return {
    s,
    content,
    rng: makeRng(s.rng),
    monthsPerTick: content.scenario.monthsPerTick,
    cashAtTickStart: s.cash,
    ledger: [],
    events: [],
    rejected: [],
    emBudget: { design: 0, verification: 0, implementation: 0 },
    allocation: {
      design: emptyAllocation(),
      verification: emptyAllocation(),
      implementation: emptyAllocation(),
    },
    productSales: [],
    revenue: 0,
    nreRevenue: 0,
    cogs: 0,
    opex: 0,
    financingInflow: 0,
  };
}

function emptyAllocation(): AllocationReport {
  return { budget: 0, nre: 0, sustaining: 0, qual: 0, research: 0, reliability: 0, tools: 0, tree: 0, byProject: {}, idle: 0 };
}

/** Post a ledger entry (integer dollars; positive = inflow) and apply it to cash. */
export function charge(ctx: TickContext, category: LedgerCategory, amount: number, note: string): void {
  const rounded = Math.round(amount);
  if (rounded === 0) return;
  ctx.ledger.push({ month: ctx.s.monthIndex, category, amount: rounded, note });
  ctx.s.cash += rounded;
}

export function emit(ctx: TickContext, type: EngineEvent["type"], message: string, data: EngineEvent["data"] = {}): void {
  ctx.events.push({ month: ctx.s.monthIndex, type, message, data });
}
